import React, { useContext, useMemo } from 'react';

import { Skeleton } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import QuickAccess from '../../../../shared/components/v3/Buttons/QuickAccess/QuickAccess';
import { REPORTS_ROUTE } from '../../../../shared/constants/auth';
import { PAYMENT_LOCALES } from '../../../../shared/constants/banner';
import {
  ACCESS_TO_REPORTS,
  ACCESS_DIRECT_PAYMENT,
  ACCESS_DIRECT_REDIRECT_BUTTONS,
  ACCESS_REQUESTS
} from '../../../../shared/constants/buttons';
import { EXTERNAL_PAYMENT_ERROR_MODAL } from '../../../../shared/constants/modal';
import { PLAN_TYPE_FR } from '../../../../shared/constants/plan';
import { CAN_SEE_METRICS_AND_REPORTS, CAN_SEE_REQUESTS_FG } from '../../../../shared/constants/statsig';
import { CLICK_ACTION, GO_TO_REPORTS, QUICK_ACCESS, SECTION_HOME } from '../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { ModalContext } from '../../../../shared/context/modal/modalContext';
import { PlansContext } from '../../../../shared/context/plans/plansContext';
import Company from '../../../../shared/services/api/companies/companies';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_HOME } from '../../../../shared/translations/home';
import { redirectToPage } from '../../../../shared/utils/utils';

import './DirectAccess.scss';

const DirectAccess = () => {
  const { companyData, isLoadingCompanyData, isSpain, companyPlansType } = useContext(CompanyContext);
  const { handleOpenModal } = useContext(ModalContext);
  const { plans } = useContext(PlansContext);
  const accessToReportsText = isSpain
    ? TRANSLATIONS_HOME.quickAccess.report.rf
    : TRANSLATIONS_HOME.quickAccess.report.default;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useStatsigClient();

  const hasRequestsAccess = useMemo(() => client?.checkGate(CAN_SEE_REQUESTS_FG), [client]);
  const hasReportAccess = useMemo(() => client?.checkGate(CAN_SEE_METRICS_AND_REPORTS), [client]);
  const hasRequestsSection = plans.some((p) => p.supportRequests);

  const displayPaymentButton = PAYMENT_LOCALES.includes(companyData.locale.toLowerCase());

  const displayRequestsButton = companyPlansType === PLAN_TYPE_FR;

  const sendTagManagerEvent = (id: string, redirectLink?: string) => {
    const quickAccessGtmParams = {
      quick_access_id: id,
      quick_access_url: redirectLink
    };
    TagManagerService.pushEvent(CLICK_ACTION, QUICK_ACCESS, quickAccessGtmParams);
  };

  const quickAccessAction = (id: string, redirectLink: string) => {
    try {
      redirectToPage(navigate, redirectLink);
      sendTagManagerEvent(id, redirectLink);
    } catch {
      handleOpenModal();
    }
  };

  const paymentAccessAction = async (id: string) => {
    try {
      const response = await Company.getPaymentUrl(companyData.id);
      sendTagManagerEvent(id, response.url);
      window.open(response.url);
    } catch {
      handleOpenModal(EXTERNAL_PAYMENT_ERROR_MODAL);
    }
  };

  const renderPaymentButton = () => {
    if (displayPaymentButton) {
      return (
        <QuickAccess
          icon={ACCESS_DIRECT_PAYMENT.icon}
          description={t(ACCESS_DIRECT_PAYMENT.description)}
          action={() => paymentAccessAction(ACCESS_DIRECT_PAYMENT.id)}
        />
      );
    }
    return <></>;
  };

  const goToReports = () => {
    TagManagerService.pushEvent(CLICK_ACTION, GO_TO_REPORTS, SECTION_HOME);
    redirectToPage(navigate, REPORTS_ROUTE);
  };

  if (isLoadingCompanyData) {
    return (
      <div className="direct-access-content-v3">
        <div className="direct-access-label">{t(TRANSLATIONS_HOME.shortCuts)}</div>
        <div className="flex row gap-6">
          <Skeleton className="skeleton-direct-access" variant="rectangular" />
          <Skeleton className="skeleton-direct-access" variant="rectangular" />
          <Skeleton className="skeleton-direct-access" variant="rectangular" />
          <Skeleton className="skeleton-direct-access" variant="rectangular" />
        </div>
      </div>
    );
  }

  return (
    <div className="direct-access-content-v3" data-experience="new">
      <div className="direct-access-label">{t(TRANSLATIONS_HOME.shortCuts)}</div>
      <div className="flex row gap-6">
        {hasReportAccess && (
          <QuickAccess icon={ACCESS_TO_REPORTS.icon} description={t(accessToReportsText)} action={goToReports} />
        )}
        {ACCESS_DIRECT_REDIRECT_BUTTONS?.map((item) => (
          <QuickAccess
            key={uuid()}
            icon={item.icon}
            description={t(item.description)}
            action={() => quickAccessAction(item.id, item.redirectLink)}
          />
        ))}
        {renderPaymentButton()}
        {displayRequestsButton && hasRequestsAccess && hasRequestsSection && (
          <QuickAccess
            icon={ACCESS_REQUESTS.icon}
            description={t(ACCESS_REQUESTS.description)}
            action={() => quickAccessAction(ACCESS_REQUESTS.id, ACCESS_REQUESTS.redirectLink)}
          />
        )}
      </div>
    </div>
  );
};

export default DirectAccess;
